import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Ticket as TicketIcon } from 'lucide-react';
import {
  TICKET_STATUS_VALUES,
  TICKET_STATUS_LABELS,
  type TicketStatus,
  type TicketSummary,
} from '@electrodavisan/shared';
import { ROUTES } from '../../app/config';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Pagination,
  Select,
  SkeletonList,
  Table,
  TicketPriorityBadge,
  TicketStatusBadge,
  type Column,
} from '../../components/ui';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useMyTickets } from '../../services/tickets.service';

const PAGE_SIZE = 20;

export function MyTicketsPage(): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, refetch } = useMyTickets({
    page,
    pageSize: PAGE_SIZE,
    status: status ? ([status] as TicketStatus[]) : undefined,
    search: search || undefined,
  });

  const columns: Array<Column<TicketSummary>> = [
    {
      key: 'number',
      header: 'Ticket',
      render: (ticket) => (
        <span className="font-mono text-[13px] text-text-primary">{ticket.ticketNumber}</span>
      ),
    },
    {
      key: 'subject',
      header: 'Asunto',
      render: (ticket) => (
        <div className="min-w-0 max-w-sm">
          <p className="truncate font-medium text-text-primary">{ticket.subject}</p>
          <p className="truncate text-[13px] text-text-muted">
            {ticket.applicationName ?? 'General'}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (ticket) => <TicketStatusBadge status={ticket.status} />,
    },
    {
      key: 'priority',
      header: 'Prioridad',
      hideBelow: 'md',
      render: (ticket) => <TicketPriorityBadge priority={ticket.priority} />,
    },
    {
      key: 'agent',
      header: 'Agente',
      hideBelow: 'lg',
      render: (ticket) => (
        <span className="text-[13px]">{ticket.assignedToName ?? 'Sin asignar'}</span>
      ),
    },
    {
      key: 'updated',
      header: 'Actualizado',
      hideBelow: 'sm',
      render: (ticket) => (
        <span className="whitespace-nowrap text-[13px]" title={formatDateTime(ticket.lastActivityAt)}>
          {formatRelative(ticket.lastActivityAt)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Mis tickets"
        description="Todos los tickets que has creado y su estado actual."
        actions={
          <Link to={ROUTES.newTicket}>
            <Button leftIcon={<Plus className="h-4 w-4" aria-hidden />}>Crear ticket</Button>
          </Link>
        }
      />

      <Card className="mb-4 p-4">
        <form
          className="grid gap-3 sm:grid-cols-[1fr_auto_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(searchInput.trim());
            setPage(1);
          }}
        >
          <Input
            placeholder="Buscar por numero o asunto"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            aria-label="Buscar tickets"
          />
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
            placeholder="Todos los estados"
            options={TICKET_STATUS_VALUES.map((value) => ({
              value,
              label: TICKET_STATUS_LABELS[value],
            }))}
            aria-label="Filtrar por estado"
            containerClassName="sm:w-52"
          />
          <Button type="submit" variant="secondary" leftIcon={<Search className="h-4 w-4" aria-hidden />}>
            Buscar
          </Button>
        </form>
      </Card>

      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <SkeletonList rows={5} />
      ) : (
        <>
          <Table
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(ticket) => ticket.id}
            onRowClick={(ticket) => navigate(ROUTES.ticketDetail(ticket.id))}
            empty={
              <EmptyState
                icon={<TicketIcon className="h-6 w-6" aria-hidden />}
                title={
                  search || status
                    ? 'Ningun ticket coincide con el filtro'
                    : 'Todavia no has creado tickets'
                }
                description={
                  search || status
                    ? 'Prueba a cambiar los criterios de busqueda.'
                    : 'Crea un ticket cuando necesites ayuda con una aplicacion o con el Ecosystem.'
                }
                action={
                  !search && !status ? (
                    <Link to={ROUTES.newTicket}>
                      <Button leftIcon={<Plus className="h-4 w-4" aria-hidden />}>
                        Crear mi primer ticket
                      </Button>
                    </Link>
                  ) : null
                }
              />
            }
          />

          {data ? (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPageChange={setPage}
            />
          ) : null}
        </>
      )}
    </>
  );
}
